import type { ReturnLineState, ReturnRequestView } from '@miraki/contracts';
import { ApiError } from '../../api/client';

/**
 * R-25 and R-20's return draft — what the screens decide about a request, kept pure and tested.
 * The rules themselves are the server's (`returns.rules.ts`); this only decides what to OFFER.
 */

/**
 * CN-13 — whether to offer Withdraw. Before any piece is decided, not already withdrawn, and with
 * something still REQUESTED. The server refuses it regardless (a decision can land between this
 * read and the click); this keeps the button off a request where it can only fail.
 */
export function mayWithdraw(r: Pick<ReturnRequestView, 'withdrawnAt' | 'counts'>): boolean {
  const decided = r.counts.APPROVED + r.counts.REFUSED + r.counts.RECEIVED;
  return r.withdrawnAt === null && decided === 0 && r.counts.REQUESTED > 0;
}

export type Tone = 'us' | 'them' | 'done' | 'stop' | 'run' | 'lapse';

/** How a line's state is drawn. RECEIVED is the end — the budget for it has come back (CN-14). */
export const LINE_TONE: Readonly<Record<ReturnLineState, Tone>> = {
  REQUESTED: 'us', APPROVED: 'run', REFUSED: 'stop', WITHDRAWN: 'lapse', RECEIVED: 'done', SOLD_INSTEAD: 'lapse',
};

export type Refusal = { code: string; pieceId?: string; status?: string; state?: string };

/**
 * A 409 from POST /shop/returns lists EVERY refusal (`RETURN_REFUSED`). Turned into one line per
 * refusal, naming the piece by Job Number — a piece id means nothing to the person who chose it.
 * Null when the error is not that refusal, so anything else is shown as the error it is.
 */
export function refusalLines(err: unknown, jobOf: (pieceId: string) => string | undefined):
  Array<{ key: string; values: Record<string, string> }> | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.detail as { code?: unknown; refusals?: unknown } | undefined;
  if (d?.code !== 'RETURN_REFUSED' || !Array.isArray(d.refusals)) return null;
  return (d.refusals as Refusal[]).map((r) => ({
    key: `returns.refusal.${r.code}`,
    values: { job: (r.pieceId && jobOf(r.pieceId)) ?? r.pieceId ?? '', status: r.status ?? '', state: r.state ?? '' },
  }));
}
